import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface PageSEO {
  meta_title: string
  meta_description: string
  meta_keywords: string
  og_title: string
  og_description: string
  og_image: string
  canonical_url: string
  robots: string
  structured_data: Record<string, unknown> | null
}

interface SEOProps {
  pageKey: string
  fallback?: {
    title?: string
    description?: string
  }
}

const BASE_URL = 'https://engimarinex.com'

function setMetaTag(name: string, content: string, isProperty = false) {
  const attribute = isProperty ? 'property' : 'name'
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }

  element.content = content
}

function setCanonicalUrl(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement

  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }

  link.href = url
}

function setStructuredData(data: Record<string, unknown>) {
  const id = 'structured-data-script'
  let script = document.getElementById(id) as HTMLScriptElement

  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }

  script.textContent = JSON.stringify(data)
}

export function SEO({ pageKey, fallback }: SEOProps) {
  const { data: seo } = useQuery({
    queryKey: ['page-seo', pageKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_seo')
        .select('*')
        .eq('page_key', pageKey)
        .single()
      if (error) return null
      return data as PageSEO
    },
    staleTime: 1000 * 60 * 10, // 10 minutes cache
  })

  useEffect(() => {
    const title = seo?.meta_title || fallback?.title || 'EngiMarineX - Marine Equipment'
    const description = seo?.meta_description || fallback?.description || ''
    const keywords = seo?.meta_keywords || ''
    const ogTitle = seo?.og_title || title
    const ogDescription = seo?.og_description || description
    const ogImage = seo?.og_image || `${BASE_URL}/images/og-default.jpg`
    const canonicalUrl = seo?.canonical_url || `${BASE_URL}${pageKey === 'home' ? '' : `/${pageKey}`}`
    const robots = seo?.robots || 'index, follow'

    // Set title
    document.title = title

    // Primary Meta Tags
    setMetaTag('title', title)
    setMetaTag('description', description)
    if (keywords) setMetaTag('keywords', keywords)
    setMetaTag('robots', robots)
    setCanonicalUrl(canonicalUrl)

    // Open Graph / Facebook
    setMetaTag('og:type', 'website', true)
    setMetaTag('og:url', canonicalUrl, true)
    setMetaTag('og:title', ogTitle, true)
    setMetaTag('og:description', ogDescription, true)
    setMetaTag('og:image', ogImage, true)
    setMetaTag('og:site_name', 'EngiMarineX', true)
    setMetaTag('og:locale', 'en_US', true)

    // Twitter
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:url', canonicalUrl)
    setMetaTag('twitter:title', ogTitle)
    setMetaTag('twitter:description', ogDescription)
    setMetaTag('twitter:image', ogImage)

    // Structured Data
    if (seo?.structured_data) {
      setStructuredData(seo.structured_data)
    }
  }, [seo, pageKey, fallback])

  return null
}
